import {
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  FilterOutlined,
  DownloadOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { MenuProps, TableProps } from 'antd';
import { Button, Dropdown, Input, Radio, Select, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { PreviewImage } from '@/components/PreviewImage/PreviewImage';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import { useGetInventoryQuery, useGetWarehousesQuery } from '@/modules/inventory/inventoryApi';
import { useAppSelector } from '@/store/hooks';
import type { InventoryItem, InventoryStockStatus } from '@/types/inventory';
import { downloadAuthenticatedFile } from '@/utils/downloadFile';
import { formatDateTime } from '@/utils/date';
import { getMediaImageUrl } from '@/utils/media';
import { ucfirst } from '@/utils/string';
import { ColumnFilterPanel } from '../categories/components/ColumnFilterPanel';
import { SortableColumnTitle } from '../categories/components/SortableColumnTitle';
import { AdjustStockModal } from './components/AdjustStockModal';
import { InventorySummarySidebar } from './components/InventorySummarySidebar';
import { useInventoryQueryParams } from './hooks/useInventoryQueryParams';
import styles from './Inventory.module.scss';

function getStockClass(quantity: number): string {
  if (quantity <= 0) return styles.outOfStock;
  if (quantity <= 10) return styles.lowStock;
  return styles.inStock;
}

export default function Inventory() {
  const navigate = useNavigate();
  const notify = useNotify();
  const { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort } =
    useInventoryQueryParams();
  const { data: warehouses = [] } = useGetWarehousesQuery();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const {
    data: listResult,
    isLoading,
    isFetching,
    isError: isInventoryError,
    error: inventoryError,
  } = useGetInventoryQuery(apiParams);
  const summary = listResult?.summary;
  useGlobalLoader(isFetching && !!listResult);

  useEffect(() => {
    if (!isInventoryError) return;
    const message =
      typeof inventoryError === 'object' && inventoryError && 'data' in inventoryError
        ? String(inventoryError.data)
        : 'Could not load inventory. Restart the API server and try again.';
    notify.error(message);
  }, [inventoryError, isInventoryError, notify]);

  const hasTableFilters = hasActiveFiltersOrSort;

  const activeWarehouse = useMemo(
    () => warehouses.find((warehouse) => warehouse.id === state.warehouseId),
    [state.warehouseId, warehouses],
  );

  const items = listResult?.results ?? [];
  const activeSortBy = listResult?.sortBy ?? state.sortBy;
  const activeSortOrder = listResult?.sortOrder ?? state.sortOrder;
  const total = listResult?.total ?? 0;
  const currentPage = Number(listResult?.page ?? state.page);
  const pageSize = Number(listResult?.limit ?? state.limit);

  const [searchDraft, setSearchDraft] = useState(state.search);
  const [warehouseDraft, setWarehouseDraft] = useState(state.warehouseId);
  const [stockStatusDraft, setStockStatusDraft] = useState<InventoryStockStatus>(state.stockStatus);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const exportQuery = useMemo(() => {
    const searchParams = new URLSearchParams();
    if (state.search) searchParams.set('search', state.search);
    if (state.warehouseId) searchParams.set('warehouseId', state.warehouseId);
    if (state.stockStatus !== 'all') searchParams.set('stockStatus', state.stockStatus);
    if (state.sortBy) searchParams.set('sortBy', state.sortBy);
    if (state.sortOrder) searchParams.set('sortOrder', state.sortOrder);
    const query = searchParams.toString();
    return query ? `?${query}` : '';
  }, [state.search, state.sortBy, state.sortOrder, state.stockStatus, state.warehouseId]);

  useEffect(() => {
    if (!state.warehouseId && warehouses.length > 0) {
      const defaultWarehouse = warehouses.find((w) => w.isDefault) ?? warehouses[0];
      if (defaultWarehouse) {
        patchParams({ warehouseId: defaultWarehouse.id });
      }
    }
  }, [patchParams, state.warehouseId, warehouses]);

  const handleSort = useCallback(
    (field: string) => {
      const nextOrder: 'asc' | 'desc' =
        state.sortBy === field ? (state.sortOrder === 'asc' ? 'desc' : 'asc') : 'asc';
      patchParams({ sortBy: field, sortOrder: nextOrder, page: 1 });
    },
    [patchParams, state.sortBy, state.sortOrder],
  );

  const openAdjust = useCallback((item: InventoryItem) => {
    setEditingItem(item);
    setAdjustOpen(true);
  }, []);

  const closeAdjust = () => {
    setAdjustOpen(false);
    setEditingItem(null);
  };

  const handleResetFiltersAndSort = () => {
    setSearchDraft('');
    setWarehouseDraft('');
    setStockStatusDraft('all');
    resetAll();
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const fallback = `inventory-${(activeWarehouse?.code ?? 'warehouse').toLowerCase()}.xlsx`;
      const filename = await downloadAuthenticatedFile(
        `/inventory/export${exportQuery}`,
        accessToken,
        fallback,
      );
      notify.success(`Exported ${filename}`);
    } catch {
      notify.error('Could not export inventory. Restart the API server and try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleTableChange: TableProps<InventoryItem>['onChange'] = (pagination) => {
    patchParams({
      page: pagination.current ?? 1,
      limit: pagination.pageSize ?? pageSize,
    });
  };

  const getRowActionItems = useCallback(
    (item: InventoryItem): MenuProps['items'] => [
      {
        key: 'view',
        label: 'View product',
        icon: <EyeOutlined />,
        onClick: () => navigate(`/products/${item.productId}/view`),
      },
      {
        key: 'edit',
        label: 'Edit product',
        icon: <EditOutlined />,
        onClick: () => navigate(`/products/${item.productId}/edit`),
      },
      { type: 'divider' },
      {
        key: 'adjust',
        label: 'Adjust stock',
        onClick: () => openAdjust(item),
      },
    ],
    [navigate, openAdjust],
  );

  const columns: ColumnsType<InventoryItem> = useMemo(
    () => [
      {
        title: (
          <SortableColumnTitle
            label="PRODUCT"
            field="productName"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        key: 'product',
        className: styles.nameCol,
        filterIcon: (filtered) => (
          <SearchOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.search ? [state.search] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ search: searchDraft.trim() || null }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setSearchDraft('');
              patchParams({ search: null }, { resetPage: true });
              close();
            }}
          >
            <Input
              allowClear
              size="small"
              prefix={<SearchOutlined />}
              placeholder="Search name or SKU"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              onPressEnter={() => {
                patchParams({ search: searchDraft.trim() || null }, { resetPage: true });
                close();
              }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setSearchDraft(state.search);
          },
        },
        render: (_value, record) => (
          <Link to={`/products/${record.productId}/view`} className={styles.nameLink}>
            <div className={styles.nameRow}>
              <PreviewImage
                src={getMediaImageUrl(record.thumbnail?.imageLarge ?? record.thumbnail?.imageSmall)}
                size={36}
                className={styles.thumb}
                fallback={ucfirst(record.productName).charAt(0)}
                alt={record.productName}
              />
              <div className={styles.nameBlock}>
                <span className={styles.nameCell}>{ucfirst(record.productName)}</span>
                <span className={styles.slug}>
                  {record.sku}
                  {record.variantLabel ? ` · ${record.variantLabel}` : ''}
                </span>
              </div>
            </div>
          </Link>
        ),
      },
      {
        title: 'WAREHOUSE',
        key: 'warehouse',
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.warehouseId ? [state.warehouseId] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ warehouseId: warehouseDraft || null }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setWarehouseDraft('');
              patchParams({ warehouseId: null }, { resetPage: true });
              close();
            }}
          >
            <Select
              allowClear
              size="small"
              placeholder="All warehouses"
              value={warehouseDraft || undefined}
              onChange={(value) => setWarehouseDraft(value ?? '')}
              className={styles.filterSelect}
              options={warehouses.map((warehouse) => ({
                label: `${warehouse.name} (${warehouse.code})`,
                value: warehouse.id,
              }))}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setWarehouseDraft(state.warehouseId);
          },
        },
        render: (_value, record) => (
          <span className={styles.metaCell}>
            {record.warehouseName} ({record.warehouseCode})
          </span>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="AVAILABLE"
            field="productTotalStock"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'productTotalStock',
        key: 'productTotalStock',
        align: 'center',
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.stockStatus !== 'all' ? [state.stockStatus] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams(
                { stockStatus: stockStatusDraft === 'all' ? null : stockStatusDraft },
                { resetPage: true },
              );
              close();
            }}
            onReset={() => {
              setStockStatusDraft('all');
              patchParams({ stockStatus: null }, { resetPage: true });
              close();
            }}
          >
            <Radio.Group
              value={stockStatusDraft}
              onChange={(event) => setStockStatusDraft(event.target.value)}
              className={styles.statusRadio}
            >
              <Radio value="all">All stock</Radio>
              <Radio value="low">Low stock (≤10)</Radio>
              <Radio value="out">Out of stock</Radio>
            </Radio.Group>
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setStockStatusDraft(state.stockStatus);
          },
        },
        render: (value: number) => <span className={getStockClass(value)}>{value}</span>,
      },
      {
        title: 'SOLD',
        dataIndex: 'soldQty',
        key: 'soldQty',
        align: 'center',
        render: (soldQty: number = 0) => (
          <span className={soldQty > 0 ? styles.soldQty : styles.soldQtyEmpty}>{soldQty}</span>
        ),
      },
      {
        title: 'TYPE',
        key: 'type',
        render: (_value, record) => (
          <Tag color={record.productType === 'variable' ? 'gold' : 'default'}>
            {ucfirst(record.productType)}
          </Tag>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="UPDATED"
            field="updatedAt"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        render: (value?: string) => (
          <span className={styles.dateCell}>{value ? formatDateTime(value) : '—'}</span>
        ),
      },
      {
        title: 'ACTION',
        key: 'actions',
        align: 'center',
        render: (_value, record) => (
          <Dropdown menu={{ items: getRowActionItems(record) }} trigger={['click']}>
            <Button
              type="text"
              size="small"
              icon={<EllipsisOutlined />}
              className={styles.actionBtn}
            />
          </Dropdown>
        ),
      },
    ],
    [
      activeSortBy,
      activeSortOrder,
      getRowActionItems,
      handleSort,
      patchParams,
      searchDraft,
      state.search,
      state.stockStatus,
      state.warehouseId,
      stockStatusDraft,
      warehouseDraft,
      warehouses,
    ],
  );

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <>
            <Button
              type="default"
              size="small"
              className={styles.headerBtn}
              icon={<DownloadOutlined />}
              loading={isExporting}
              onClick={handleExport}
            >
              Export XLSX
            </Button>
            {hasActiveFiltersOrSort ? (
              <Button
                type="default"
                size="small"
                className={styles.headerBtn}
                icon={<ReloadOutlined />}
                onClick={handleResetFiltersAndSort}
              >
                Reset
              </Button>
            ) : null}
          </>
        }
      />

      <div className={styles.content}>
        <InventorySummarySidebar
          summary={summary}
          warehouse={activeWarehouse}
          isLoading={isLoading && !summary}
          isFiltered={hasTableFilters}
        />

        <div className={styles.tableCard}>
          <Table<InventoryItem>
            className={styles.table}
            rowKey="id"
            size="small"
            loading={isLoading && !listResult}
            columns={columns}
            dataSource={items}
            onChange={handleTableChange}
            pagination={{
              current: currentPage,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50],
              showTotal: (totalCount, [start, end]) => `${start}-${end} of ${totalCount}`,
              size: 'small',
            }}
          />
        </div>
      </div>

      <AdjustStockModal open={adjustOpen} item={editingItem} onClose={closeAdjust} />
    </div>
  );
}
